import { useEffect } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useVerifyPayment } from "@/hooks/useVerifyPayment";
import type { Database } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";

interface Props {
  invalidateKeys?: string[][];
  className?: string;
  client?: SupabaseClient<Database>;
}

export function PaymentReturnBanner({
  invalidateKeys = [],
  className,
  client,
}: Props) {
  const { t } = useTranslation();
  const result = useVerifyPayment({ enabled: true, client });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (result.status === "paid_and_activated" || result.status === "paid_invoice_pending") {
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    }
  }, [invalidateKeys, queryClient, result.status]);

  if (result.status === "idle") return null;

  const toneMap = {
    verifying: {
      icon: Loader2,
      iconClass: "text-primary animate-spin",
      title: t("hire.paymentReturn.verifyingTitle"),
      body: t("hire.paymentReturn.verifyingBody"),
      tone: "border-s-primary",
    },
    paid_and_activated: {
      icon: CheckCircle2,
      iconClass: "text-emerald-500",
      title: t("hire.paymentReturn.activatedTitle"),
      body: t("hire.paymentReturn.activatedBody"),
      tone: "border-s-emerald-500",
    },
    paid_activation_pending: {
      icon: Clock,
      iconClass: "text-amber-500",
      title: t("hire.paymentReturn.activationPendingTitle"),
      body: t("hire.paymentReturn.activationPendingBody"),
      tone: "border-s-amber-500",
    },
    paid_activation_failed: {
      icon: XCircle,
      iconClass: "text-destructive",
      title: t("hire.paymentReturn.activationFailedTitle"),
      body: t("hire.paymentReturn.activationFailedBody"),
      tone: "border-s-destructive",
    },
    paid_invoice_pending: {
      icon: Clock,
      iconClass: "text-amber-500",
      title: t("hire.paymentReturn.invoicePendingTitle"),
      body: t("hire.paymentReturn.invoicePendingBody"),
      tone: "border-s-amber-500",
    },
    failed: {
      icon: XCircle,
      iconClass: "text-destructive",
      title: t("hire.paymentReturn.failedTitle"),
      body: t("hire.paymentReturn.failedBody"),
      tone: "border-s-destructive",
    },
    cancelled: {
      icon: XCircle,
      iconClass: "text-muted-foreground",
      title: t("hire.paymentReturn.cancelledTitle"),
      body: t("hire.paymentReturn.cancelledBody"),
      tone: "border-s-muted-foreground",
    },
    expired: {
      icon: Clock,
      iconClass: "text-amber-500",
      title: t("hire.paymentReturn.expiredTitle"),
      body: t("hire.paymentReturn.expiredBody"),
      tone: "border-s-amber-500",
    },
    abandoned: {
      icon: Clock,
      iconClass: "text-amber-500",
      title: t("hire.paymentReturn.expiredTitle"),
      body: t("hire.paymentReturn.abandonedBody"),
      tone: "border-s-amber-500",
    },
    refunded: {
      icon: XCircle,
      iconClass: "text-muted-foreground",
      title: t("hire.paymentReturn.refundedTitle"),
      body: t("hire.paymentReturn.refundedBody"),
      tone: "border-s-muted-foreground",
    },
    pending: {
      icon: Clock,
      iconClass: "text-amber-500",
      title: t("hire.paymentReturn.pendingTitle"),
      body: t("hire.paymentReturn.pendingBody"),
      tone: "border-s-amber-500",
    },
    error: {
      icon: XCircle,
      iconClass: "text-destructive",
      title: t("hire.paymentReturn.errorTitle"),
      body: result.reason || t("hire.paymentReturn.errorBody"),
      tone: "border-s-destructive",
    },
  } as const;

  const cfg = toneMap[result.status];
  const Icon = cfg.icon;

  return (
    <Card className={`border-s-4 p-4 ${cfg.tone} ${className ?? ""}`}>
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${cfg.iconClass}`} />
        <div className="space-y-0.5">
          <p className="text-sm font-semibold text-foreground">{cfg.title}</p>
          <p className="text-xs text-muted-foreground">{cfg.body}</p>
        </div>
      </div>
    </Card>
  );
}
